import type { TowerDriver } from "../types/controlTower";
import { driverInactivityAlert, minutesSinceLastDelivery } from "../components/controlTower/driverActivity";

export type TowerDriverSort = "risk" | "flexInTransit" | "inactivity" | "progress" | "name";

// En «Por riesgo», una alerta visible va antes que el volumen: primero rojas,
// luego amarillas y «Sin entregas» / «Sin movimiento». Sin alertas, FLEX pendiente conserva
// la prioridad principal. `overallOf` devuelve el chofer completo cuando la
// fila es el recorte de una zona: la inactividad se mide con todas sus
// entregas, igual que en la tabla.
export function compareTowerDrivers(
  a: TowerDriver,
  b: TowerDriver,
  sort: TowerDriverSort,
  nowMs = Date.now(),
  alertsEnabled = true,
  overallOf: (driver: TowerDriver) => TowerDriver = (driver) => driver,
): number {
  const byName = () => a.name.localeCompare(b.name, "es") || a.id.localeCompare(b.id);
  const idleA = minutesSinceLastDelivery(overallOf(a).lastDelivery, nowMs);
  const idleB = minutesSinceLastDelivery(overallOf(b).lastDelivery, nowMs);
  // Rango de la alerta: crítica, leve, sin entregas, ninguna. Entre dos
  // alertas con hora, primero la que lleva más tiempo sin entregar.
  const alertOf = (driver: TowerDriver) =>
    alertsEnabled ? driverInactivityAlert(driver, overallOf(driver), nowMs) : null;
  const alertA = alertOf(a);
  const alertB = alertOf(b);
  const rank = (alert: typeof alertA) =>
    alert?.level === "critical" ? 3 : alert?.level === "warning" ? 2 : alert ? 1 : 0;
  const timedIdle = (alert: typeof alertA) => (alert && "idleMinutes" in alert ? alert.idleMinutes : -1);
  const riskA = rank(alertA);
  const riskB = rank(alertB);
  if (sort === "name") return byName();
  if (sort === "risk") {
    const remainingA = a.counts.inTransit + a.counts.pending;
    const remainingB = b.counts.inTransit + b.counts.pending;
    return (
      riskB - riskA ||
      timedIdle(alertB) - timedIdle(alertA) ||
      b.counts.flexInTransit - a.counts.flexInTransit ||
      (idleB ?? -1) - (idleA ?? -1) ||
      b.counts.incident - a.counts.incident ||
      remainingB - remainingA ||
      a.progress - b.progress ||
      byName()
    );
  }
  if (sort === "flexInTransit")
    return (
      b.counts.flexInTransit - a.counts.flexInTransit || riskB - riskA || (idleB ?? -1) - (idleA ?? -1) || byName()
    );
  if (sort === "inactivity")
    return (idleB ?? -1) - (idleA ?? -1) || b.counts.flexInTransit - a.counts.flexInTransit || byName();
  return b.progress - a.progress || b.counts.flexInTransit - a.counts.flexInTransit || byName();
}
